import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {type FontData, isInternalFont, USER_FONT_LICENSE} from '@Libraries/font-library';
import {PosterEditingBaseFontSelector} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-base-font-selector';
import type {BrandFont} from '@Components/editor-brands/editor-brands.types';
import {onAddBrandAssetClickedHandler} from '@Components/poster-editor/library/poster-editor-library';
import {useGetSelectedTranscriptItem} from '@Hooks/poster-editor/useGetSelectedTranscriptItem';
import type {TranscriptItemFontSelectorProps} from './transcript-item-font-selector.types';
import {useGetKeyFromSubtitleTextStylesForTranscriptItem} from '@Hooks/poster-editor/useGetKeyFromSubtitleTextStylesForTranscriptItem';

export function TranscriptItemFontSelector({
  isForMobile = false,
  onClose = (): void => {},
  onBack = (): void => {},
  showCloseButton = false,
  showBackButton = true,
}: TranscriptItemFontSelectorProps): ReactElement<any> {
  const activeItem = useGetSelectedTranscriptItem();
  if (activeItem === null) {
    throw new Error('Transcript Item is undefined in TranscriptItemFontSelector');
  }

  const fontFamily = useGetKeyFromSubtitleTextStylesForTranscriptItem('fontFamily', false);
  if (fontFamily === undefined) {
    throw new Error('Transcript Item does not have fontFamily property in sentenceTextStyles');
  }

  const fontLicense = useGetKeyFromSubtitleTextStylesForTranscriptItem('fontLicense', false);
  if (fontLicense === undefined) {
    throw new Error('Transcript Item does not have fontLicense property in sentenceTextStyles');
  }

  const script = useGetKeyFromSubtitleTextStylesForTranscriptItem('script', false);
  if (script === undefined) {
    throw new Error('Transcript Item does not have script property in sentenceTextStyles');
  }

  const [temporaryState, setTemporaryState] = useState<string>('');
  const [isNewFontLoading, setIsNewFontLoading] = useState(false);

  useEffect(() => {
    setTemporaryState(fontFamily);
  }, [fontFamily]);

  const onSelectFontFamily = (fontFamily: string): void => {
    setIsNewFontLoading(true);
    setTemporaryState(fontFamily);
    const fontLicense = isInternalFont(fontFamily) ? '' : USER_FONT_LICENSE;
    const oldFontFamily = fontFamily;
    const oldFontLicense = fontLicense;
    activeItem
      .updateAllSubtitlesStylesFromSubtitleObject({
        sentenceTextStyles: {fontFamily, fontLicense},
        activeWordTextStyles: {fontFamily, fontLicense},
      })
      .then((): void => {})
      .catch((): void => {
        activeItem.getAnySubtitle().sentenceTextStyles.fontFamily = oldFontFamily;
        activeItem.getAnySubtitle().sentenceTextStyles.fontLicense = oldFontLicense;
        setTemporaryState(fontFamily);
      })
      .finally((): void => {
        setIsNewFontLoading(false);
      });
  };

  return (
    <PosterEditingBaseFontSelector
      fontsInDesign={window.posterEditor?.whiteboard?.getFontsOnPoster() ?? []}
      onBrandItemClicked={(brand: BrandFont): void => {
        onSelectFontFamily(brand.fontFamily);
      }}
      isSelectedFontLoading={isNewFontLoading}
      selectedFontFamily={isNewFontLoading ? temporaryState : fontFamily}
      onSelectFontFamily={(font: FontData): void => {
        onSelectFontFamily(font.fontFamily);
      }}
      scriptForRecommendedFonts={script}
      showHeader
      onBackButtonCallback={(): void => {
        onBack();
      }}
      onAddBrandAsset={(): void => {
        void onAddBrandAssetClickedHandler();
      }}
      onCloseButtonCallback={(): void => {
        onClose();
      }}
      showCloseButton={showCloseButton}
      showBackButton={showBackButton}
      hasSmallTopPadding={isForMobile}
    />
  );
}
